import Vue from "vue";
import axios from "axios";
import { ActionContext } from "vuex";
import mobile from "is-mobile";
import { LocaleMessageObject } from "vue-i18n";
import { cookieBanner } from '@byted/web-privacy-sdk';
import TYPES from "./types";
import { getLangData, getLangDataFromLocal } from "~~/utils/starling";
import request from "~~/utils/request";
import { index as api } from "~~/const/apis";
import { countryLangMap } from "~~/const/locale";
import { ILoginInfo } from "~/types/form";
import { queryToJson, setCookie } from "~/utils/common";
import { ADV_INFO, BC_INFO } from "~/types/account";
import { UserRegisterMode , emailTerms } from "~/const/userRegister";
import { ContentfulConfigFromTCC } from '~/types/store';
import { setContentType, contentTypeMap } from '~/utils/contentful';
import { getRealLang } from '~/const/locale';
import { abtest } from '~/const/apis'
import eventBus from '~/utils/eventBus'

// eslint-disable-next-line no-undef
const ssoDomain = SSO_DOMAIN;
// eslint-disable-next-line no-undef
const getStartedDomain = GET_STARTED_DOMAIN;

export enum ABTestResType {
  MATCH_NOT_TEST = 4,
  MATCH_TEST = 5
}
export interface ABTestConfig {
  env_flag: 0 | 1;
  parameters: Record<string, any>;
  rsp_type: ABTestResType;
  version_name: string;
}

export interface RootState {
  lang: string;
  isLogin: boolean;
  signature: string;
  langCache: Record<string, any>;
  gotCookie: boolean;
  loginInfo: ILoginInfo;
  refer: string;
  loginTicket: string;
  loginChecked: boolean;
  hasFetchLoginInfo: boolean;
  fromRegister: boolean;
  curPageName: string;
  initI18nMessage: any;
  ABTestConfig: ABTestConfig;
  indexABTestConfig: ABTestConfig;
  ttaId: string;
  user: {
    needCookieBanner: boolean;
    userLocation: any;
  };
  advList: ADV_INFO[];
  contentType: string;
  globalLoading: boolean;
  customContentTypes: Record<string, string>;
  isCashActive: boolean;
  isDynamicLP: boolean;
  advABTestConfig: Record<string, any>;
  showCopilot: boolean;
  copilotLangWhitelist: string[];
  customRedirectedLink: string;
}

export const state = () => ({
  lang: "",
  isLogin: false,
  signature: "",
  langCache: {},
  gotCookie: false,
  loginChecked: false,
  hasFetchLoginInfo: false,
  fromRegister: false,
  loginInfo: {
    id: "",
    email: "",
    name: "",
    mobile: "",
    phonenumber: ""
  },
  refer: "",
  curPageName: "page",
  userCountry: null,
  initI18nMessage: {},
  ABTestConfig: {},
  indexABTestConfig: {},
  ttaId: "",
  advList: [],
  contentType: contentTypeMap.phase2,
  globalLoading: false,
  customContentTypes: {},
  isCashActive: undefined,
  isDynamicLP: false,
  advABTestConfig: {},
  showCopilot: false,
  copilotLangWhitelist: [],
  customRedirectedLink: undefined,
});

export const mutations = {
  [TYPES.SET_LANG](state: RootState, lang: string) {
    // setCookie时需要和下游TTAM对齐, 比如 es-419,es-MX 要 fallback到 es
    state.lang = getRealLang(lang);
    setCookie("lang_type", state.lang);
  },
  [TYPES.SET_LOGIN](state: RootState, isLogin: boolean) {
    state.isLogin = isLogin;
  },
  [TYPES.SET_LOGIN_INFO](state: RootState, data: any) {
    state.loginInfo = {
      ...(data || {}),
      phonenumber: (data || {}).mobile
    };
  },
  [TYPES.SET_GOT_COOKIE](state: RootState) {
    state.gotCookie = true;
  },
  [TYPES.SET_QUERY_REFER](state: RootState, refer: string) {
    state.refer = refer;
  },
  [TYPES.SET_LOGIN_CHECKED](state: RootState, checked: boolean) {
    state.loginChecked = checked;
  },
  [TYPES.SET_HAS_FETCH_LOGIN_INFO](state: RootState, val: boolean) {
    state.hasFetchLoginInfo = val;
  },
  [TYPES.SET_FROM_REGISTER](state: RootState, fromRegister: boolean) {
    state.fromRegister = fromRegister;
  },
  [TYPES.SET_CUR_PAGE_NAME](state: RootState, curPageName: string) {
    state.curPageName = curPageName;
  },
  [TYPES.SET_INIT_I18N_MESSAGE](state: RootState, message: any) {
    state.initI18nMessage = message;
  },
  [TYPES.SET_ABTEST_CONFIG](state: RootState, data: ABTestConfig) {
    state.ABTestConfig = data;
  },
  [TYPES.SET_TTA_ID](state: RootState, data: string) {
    state.ttaId = data;
  },
  [TYPES.SET_ADV_LIST](state: RootState, data: ADV_INFO[]) {
    state.advList = data;
  },
  [TYPES.SET_CONTENTFUL_CONTENT_TYPE](state: RootState, data: string) {
    state.contentType = data;
  },
  [TYPES.SET_GLOBAL_LOADING](state: RootState, data: boolean) {
    state.globalLoading = data;
  },
  [TYPES.SET_CUSTOM_CONTENTFUL_CONTENT_TYPE](state: RootState, data: Record<string, string>) {
    state.customContentTypes = data;
  },
  [TYPES.SET_IS_CASH_ACTIVE](state: RootState, status: boolean) {
    state.isCashActive = status;
  },
  [TYPES.SET_IS_DYNAMIC_LP](state: RootState, isDynamic: boolean) {
    state.isDynamicLP = isDynamic;
  },
  [TYPES.SET_ADV_ABTEST_CONFIG](state: RootState, data: Record<string, any>) {
    state.advABTestConfig = data;
  },
  [TYPES.SET_INDEX_ABTEST_CONFIG](state: RootState, data: ABTestConfig) {
    state.indexABTestConfig = data;
  },
  [TYPES.SET_SHOW_COPILOT](state: RootState, showCopilot: boolean) {
    state.showCopilot = showCopilot;
  },
  [TYPES.SET_COPILOT_LANG_WL](state: RootState, copilotLangWhitelist: string[]) {
    state.copilotLangWhitelist = copilotLangWhitelist;
  },
  [TYPES.SET_CUSTOM_REDIRECTED_LINK](state: RootState, customRedirectedLink: string) {
    state.customRedirectedLink = customRedirectedLink;
  },
};

export const getters = {
  isMobile: () => {
    try {
      const winWidth = window.innerWidth;
      return winWidth <= 576;
    } catch (e) {
      return false;
    }
  },
  isTablet: () => {
    try {
      const winWidth = window.innerWidth;
      return winWidth <= 768;
    } catch (e) {
      return false;
    }
  },
  isMobileDevice: () => {
    return mobile();
  },
  langType: (state: any) => {
    return state.lang || countryLangMap.default;
  },
  userRegisterMode: (state: any) => {
    // 添加 mode 方便测试，上线可不移除
    if(process.client) {
      const { mode } = queryToJson();
    // @ts-ignore
      if(UserRegisterMode[mode]) {
        return mode
      }
    }

    return state.ABTestConfig?.parameters?.getstarted_user_register?.mode || UserRegisterMode.old
  },
  ABSlug: (state: any) => {
    return state.ABTestConfig?.parameters?.split_slug?.slug || ''
  },
  ABTargetUrl: (state: any) => {
    return state.ABTestConfig?.parameters?.split_slug?.target_url || ''
  },
  ABContentType: (state: any) => {
    return state.ABTestConfig?.parameters?.split_slug?.content_type || ''
  },
  isTargetTTAB: (state: any) => {
    return typeof state.ABTestConfig?.parameters?.getstarted_user_register !=='undefined'
  },
  googleABTestConfig: (state: any) => {
    return state.ABTestConfig?.parameters?.googleTest || {
      enable: false,
      above: false,
      forceBindMobile: false
    }
  },
  mobile_style: (state: any) => {
    return state.ABTestConfig?.parameters?.common_ab?.mobile_style ?? 'old'
  },
  ABAgencySupport: (state: any) => {
    return state.ABTestConfig?.parameters?.agency_activation?.agency_support || ''
  }
};

export const actions = {
  async updateLanguage(
    { commit }: ActionContext<RootState, RootState>,
    options: {
      lang: string;
      vm: Vue;
    }
  ) {
    const { lang, vm } = options;
    const i18n = vm.$i18n as any;

    commit(TYPES.SET_LANG, lang);
    // cookie banner更新翻译
    cookieBanner.updateBannerLocale(getRealLang(lang));

    if (vm?.$route?.query?.lang === lang) {
      eventBus.$emit('updateLangParam')
      return
    }

    try {
      await vm?.$router?.replace?.({ query: {
          ...vm?.$route?.query,
          lang
        }})
      eventBus.$emit('updateLangParam')
    } catch (e) {
      console.log(e)
    }

    return (
      getLangData(vm.$route, lang)
        .then((messages: any) => messages)
        // 本地文案作为多语言兜底文案
        .catch(() => getLangDataFromLocal(vm.$route, lang))
        .then((messages: LocaleMessageObject) => {
          Object.keys(messages).forEach(language => {
            i18n.mergeLocaleMessage(language, messages[language]);
          });
          i18n.locale = lang;
          commit(TYPES.SET_LANG, lang);
        })
    );
  },
  async ssoCheckLogin({ commit, state }: ActionContext<RootState, RootState>) {
    let isLogin = false;
    let ticket = "";
    try {
      const res: any = await axios.post(
        `${ssoDomain}/check_login/`,
        {
          service: getStartedDomain,
          aid: 1583,
          appId: 1583,
          lang: state.lang || "en"
        },
        {
          withCredentials: true,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            "X-Requested-With": "XMLHttpRequest"
          }
        }
      );
      isLogin = res.data.has_login;
      ticket = res.data.ticket;
    } catch (e) {
      console.log(e)
    }
    return { isLogin, ticket };
  },
  getLoginInfo({ commit }: ActionContext<RootState, RootState>) {
    return request
      .get(api.login_info)
      .then((res: any = {}) => {
        const userInfo = res.code === 0 ? res.data?.core_user : {};
        commit(TYPES.SET_LOGIN_INFO, userInfo);
        return userInfo;
      })
      .catch((e: Error) => {
        commit(TYPES.SET_LOGIN_INFO, {});
        return {};
      });
  },
  getAdvList({ state, commit, dispatch }: ActionContext<RootState, RootState>) {
    const payload = { result_type: 0, sort_type: 0 }
    return request
      .get(api.adv_list, payload)
      .then(async (res: any) => {
        const advList: BC_INFO[] = res?.data?.data || [];
        // extract adv_id from BC account (role = "99")
        // sort to get the last visited adv
        const BC_ACCOUNT_ROLE = "99";
        const expandedAdvList = advList
          .flatMap((adv: BC_INFO) => adv.role === BC_ACCOUNT_ROLE ? adv.child : [adv])
          .sort((a, b) => b.visit_time - a.visit_time);

        commit(TYPES.SET_ADV_LIST, expandedAdvList);

        // whitelist if applicable based on new adv info
        // @ts-ignore
        await this.$whitelistService.whiteListCurrentAdvertiser(expandedAdvList)

        if (state.isDynamicLP && expandedAdvList.length > 0) {
          dispatch('getCashActive', expandedAdvList[0].id)
        }
        return expandedAdvList;
      })
      .catch(() => {
        commit(TYPES.SET_ADV_LIST, []);
        return [];
      });
  },

  getCashActive({ commit }: ActionContext<RootState, RootState>, adv_id: string) {
    const api_url = `${api.cash_active_status}?id=${adv_id}`;
    return request
      .get(api_url)
      .then((res: any) => {
        const status = res?.result === "1"
        commit(TYPES.SET_IS_CASH_ACTIVE, status);
        return status;
      })
      .catch((e: Error) => {
        console.log(e)
        return undefined
      })
  },


  getWhaleToken({ commit }: ActionContext<RootState, RootState>) {
    return Promise.resolve();
  },
  setLoginCookie({ commit }: ActionContext<RootState, RootState>, data: any) {
    return request.get(api.set_cookie, data);
  },
  nuxtServerInit(
    { commit }: { commit: any },
    { req }: { req: { contentfulConfigFromTCC: ContentfulConfigFromTCC } }
  ) {
    const contentConfig = setContentType(req)
    commit(TYPES.SET_CONTENTFUL_CONTENT_TYPE, contentConfig.contentType)
    commit(TYPES.SET_CUSTOM_CONTENTFUL_CONTENT_TYPE, contentConfig.customContentTypes)
  },
  getUserInfo() {
    return request
    .get(api.account_info)
  },
  getEmailTerms() {
    return request
    .get(api.notice, {
      platform_id: 1,
    })
  },
  setEmailTerms(_ctx: any,flag:boolean) {
    return request
    .post(api.notice, {
      platform_id: 1,
      notifications: [
        {
          notification_type: emailTerms.NEW_EVENT,
          email_enable: flag,
        },
        {
          notification_type: emailTerms.NEW_FEATURE,
          email_enable: flag,
        },
        {
          notification_type: emailTerms.PROMOTION,
          email_enable: flag,
        },
      ],
    })
  },
  getABTestConfigByAdvId({ commit }: ActionContext<RootState, RootState>, advertiserId: string) {
    return request
      .post(abtest.getABConfigByAdv, {
        advId: advertiserId
      })
      .then((res: any) => {
        const adv_abconfig  = res?.data?.adv_abconfig || {};
        commit(TYPES.SET_ADV_ABTEST_CONFIG, adv_abconfig);
        return adv_abconfig;
      })
      .catch(() => {
        return {};
      });
  },
  getABConfigByFeature({ commit }: ActionContext<RootState, RootState>, ab_feature: string) {
    return request
      .post(abtest.getABConfigByFeature, {
        ab_feature
      })
      .then((res: any) => {
        return res;
      })
      .catch(() => {
        return {};
      });
  },
};
